import {VipConfigVO} from './vip.service';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {vipOptionsV2} from './vip.variable';
import {Trans, useTranslation} from 'react-i18next';
import {cn, goTo, toPriceStr} from '@/utils';
import styles from './vip.module.scss';
interface VipCardProps {
  login: boolean;
  vip: VipConfigVO;
  lastVip?: VipConfigVO;
  currentVipLevel?: number;
  currentVipDegree?: number;
}
import {Button, ProgressBar} from 'antd-mobile';

export const VipMaxLevelContext = createContext<number>(
  vipOptionsV2.length - 1,
);

const VipCard: React.FC<VipCardProps> = ({
  login,
  vip,
  lastVip,
  currentVipLevel,
  currentVipDegree,
}) => {
  const {background, sign, progressLinear, curTextClass} = useMemo(() => {
    if (!vip.level || !vipOptionsV2[vip.level]) {
      return vipOptionsV2[0];
    }
    return vipOptionsV2[vip.level];
  }, [vip]);

  const {t} = useTranslation();
  const maxLevel = useContext(VipMaxLevelContext);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    setCurrent(lastVip?.nextLevelExperience || 0);
    setTimeout(() => {
      setCurrent(vip.currentExperience || 0);
    }, 100);
  }, [vip, lastVip]);
  const nowDiff = useMemo(() => {
    if (current) {
      return Math.max(
        Number(current) - Number(lastVip?.nextLevelExperience || 0),
        0,
      );
    }
    return 0;
  }, [current, lastVip]);
  const nowDiffStatic = useMemo(() => {
    if (vip.currentExperience) {
      return Math.max(
        Number(vip.currentExperience) -
          Number(lastVip?.nextLevelExperience || 0),
        0,
      );
    }
    return 0;
  }, [vip.currentExperience, lastVip]);
  const nextDiff = useMemo(() => {
    if (vip.nextLevelExperience) {
      if (vip.level != null && vip.level <= (currentVipLevel || 0)) {
        return Math.max(
          Number(vip.nextLevelExperience) -
            Number(lastVip?.nextLevelExperience || 0),
          0,
        );
      } else {
        return (
          (currentVipDegree || 0) +
          Math.max(
            Number(vip.nextLevelExperience) - Number(vip.currentExperience),
            0,
          )
        );
      }
    }
    return 1;
  }, [
    vip.nextLevelExperience,
    vip.level,
    vip.currentExperience,
    currentVipLevel,
    lastVip?.nextLevelExperience,
    currentVipDegree,
  ]);

  return (
    <div
      className="w-[21rem] h-[10.5rem] bg-full bg-no-repeat relative"
      style={{
        backgroundImage: `url(${background})`,
      }}>
      <div
        className="absolute z-[1] size-[4.625rem] left-2 top-1 bg-full bg-no-repeat"
        style={{
          backgroundImage: `url(${sign})`,
        }}
      />
      <div className="flex flex-col z-[1] px-5">
        <div
          className={cn(
            'relative h-[3.75rem] flex flex-row items-center justify-end',
          )}>
          <Button
            className="w-[5.6875rem] h-8 bg-c1 text-white text-xs p-0 text-center justify-center items-center rounded-full before:rounded-full shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25),0px_1px_0px_0px_rgba(255,255,255,0.20)_inset]"
            onClick={() => {
              if (!login) {
                goTo('Login');
                return;
              }
              goTo('Recharge');
            }}>
            {t('vip.recharge')}
          </Button>
        </div>
        <div className={cn('mt-1 gap-1 flex flex-col', styles['vip-progress'])}>
          <p className={cn('text-xs', curTextClass)}>
            {toPriceStr(
              Math.min(
                nowDiffStatic,
                nextDiff > 0 ? nextDiff : Number.MAX_SAFE_INTEGER,
              ),
              {fixed: 0},
            )}{' '}
            <span
              className={cn(
                'opacity-50 text-t1',
                vip.level && vip.level >= 13 ? 'text-white' : '',
              )}>
              / {nextDiff ? toPriceStr(nextDiff, {fixed: 0}) : '-'}
            </span>
          </p>
          <ProgressBar
            style={{
              '--fill-color': progressLinear,
            }}
            percent={
              vip.nextLevelExperience
                ? (Number(nowDiff) / Number(nextDiff || 1)) * 100
                : 100
            }
          />
        </div>
        <div className="flex flex-row items-center mt-3.5 z-[1]">
          <div className={cn('text-sm flex-1', curTextClass)}>
            {vip.level != null && vip.level < maxLevel ? (
              <Trans
                i18nKey="vip.rechargeTip"
                components={[<span className="font-bold text-[#F88B1D]" />]}
                values={{
                  price: toPriceStr(
                    vip.nextLevelExperience && vip.currentExperience != null
                      ? Math.max(
                          Number(vip.nextLevelExperience) -
                            Number(vip.currentExperience),
                          0,
                        )
                      : 0,
                    {
                      fixed: 0,
                    },
                  ),
                  level: `VIP${(vip.level || 0) + 1}`,
                }}
              />
            ) : (
              t('vip.incoming')
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VipCard;
